/**
 * Typed errors the builder distinguishes (build-plan §3.6). The store/UI branches on these instead of
 * raw status codes so the conflict/expiry/no-store paths stay explicit (M3.4 reconcile UI consumes
 * `ConflictError`; M3.1 already needs `SessionExpiredError` for the login bounce and `NoStoreError`
 * for the create-store redirect).
 */

/** Base error carrying the HTTP status for callers that still want the raw code. */
import { bt } from '../i18n';

export class HttpError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}

/** 409 PAGE_VERSION_CONFLICT — a stale `@Version` on saveDraft (M3.4 reconcile path). */
export class ConflictError extends HttpError {
  constructor(message = 'This page was changed elsewhere.', body?: unknown) {
    super(409, message, body);
    this.name = 'ConflictError';
  }
}

/** Silent refresh failed / no valid session — bounce to the dashboard login. */
export class SessionExpiredError extends HttpError {
  constructor(message = 'Your session has expired.') {
    super(401, message);
    this.name = 'SessionExpiredError';
  }
}

/** 409 NO_ACTIVE_STORE — the seller has no store yet; redirect to the create-store onboarding. */
export class NoStoreError extends HttpError {
  constructor(message = 'No active store for this seller.', body?: unknown) {
    super(409, message, body);
    this.name = 'NoStoreError';
  }
}

/**
 * The tab is a superadmin READ-ONLY consultation and something tried to WRITE (any non-GET). The write
 * is refused CLIENT-SIDE before it ever reaches the network — no autosave, no publish, no create-template,
 * no upload. The server would refuse it too (403 READ_ONLY_VIEW); this only spares the round-trip, the
 * error toasts, and the autosave retry storms. The `name` lets callers swallow it quietly.
 */
export class ReadOnlyViewError extends Error {
  constructor(
    message = bt('chrome.err.read_only', 'Read-only consultation: changes are not allowed.'),
  ) {
    super(message);
    this.name = 'ReadOnlyViewError';
  }
}

export function isReadOnlyViewError(e: unknown): e is ReadOnlyViewError {
  return e instanceof ReadOnlyViewError;
}

/**
 * The ACCOUNT has moved to another store while this builder document was open (founder, 2026-09-17),
 * and the request was refused rather than sent — see `boot/storePin`.
 *
 * It is thrown in two places, both inside `withAuthRetry`: when a silent refresh hands back a token
 * naming a different store (the request is NOT replayed — replaying it would write this page into the
 * other shop), and on any write attempted after the drift was detected. Never a server answer: by the
 * time the backend could say anything, the damage would already be a row in the wrong store.
 */
export class StoreChangedError extends Error {
  constructor(
    message = bt(
      'chrome.err.store_changed',
      'You are now in another store — this editor was opened on a different one.',
    ),
  ) {
    super(message);
    this.name = 'StoreChangedError';
  }
}

export function isStoreChangedError(e: unknown): e is StoreChangedError {
  return e instanceof StoreChangedError;
}

/** No HTTP response reached us (offline, DNS, CORS-blocked, aborted). */
export class NetworkError extends Error {
  constructor(message = bt('chrome.err.network', 'Network request failed.')) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isNetworkError(e: unknown): e is NetworkError {
  return e instanceof NetworkError;
}
